// Envelope v2 (M76, docs/end-to-end-encryption.md §6c, D324–D329): the
// associated data that binds a sealed message to its chat, sender, client
// id, type, epoch and revision, a group's description or picture to its
// chat, and a family record to its family, kind, id and epoch. Pure
// functions; the shared vector (shared/contracts/envelope-v2-vector.json)
// holds Android and iPhone to the same bytes.

const encoder = new TextEncoder();

/** What a v2 message envelope is bound to (D325, D326). */
export interface MessageBinding {
  conversationId: string;
  senderId: string;
  clientId: string;
  type: string;
  epoch: number;
  revision: number;
}

/** What a group's sealed description or picture is bound to (D325). */
export interface GroupBinding {
  conversationId: string;
  clientId: string;
  which: 'description' | 'picture';
  epoch: number;
  /** One more with every change, server-enforced like messages (D326). */
  revision: number;
}

/** What a v2 family record is bound to (D325, D329). */
export interface AppBinding {
  familyId: string;
  /**
   * `link`, `doc`, `name` (a list's name), `item` (a list's item),
   * `tname` (a template's name), `titem` (a template's item).
   */
  kind: string;
  /**
   * The record it belongs to: a list item's list, a template item's
   * template; the family itself for everything else.
   */
  parentId: string;
  recordId: string;
  epoch: number;
}

const U16_MAX = 0xffff;
const U32_MAX = 0xffffffff;

/** A field that can't be bound: refused rather than bound as empty (D324). */
export class AadFieldError extends Error {
  constructor() {
    super('E2EE_AAD_FIELD');
  }
}

/**
 * Canonical v2 associated data (D324):
 * `"twinden" 00 02 lp(domain) lp(field)…` with `lp(b) = u16be(len b) ‖ b`.
 * Strings are UTF-8, integers 4-byte big-endian unsigned, byte strings as
 * they are (M78's `wrap` and `wrap-th` domains, D337).
 */
export function aadV2(
  domain: string,
  fields: Array<string | number | Uint8Array>
): Uint8Array<ArrayBuffer> {
  const parts: Uint8Array[] = [encoder.encode(domain)];
  for (const field of fields) {
    if (field instanceof Uint8Array) {
      parts.push(field);
    } else if (typeof field === 'number') {
      if (!Number.isInteger(field) || field < 0 || field > U32_MAX) {
        throw new AadFieldError();
      }
      const bytes = new Uint8Array(4);
      new DataView(bytes.buffer).setUint32(0, field, false);
      parts.push(bytes);
    } else {
      if (typeof field !== 'string' || field.length === 0) {
        throw new AadFieldError();
      }
      parts.push(encoder.encode(field));
    }
  }
  const head = encoder.encode('twinden');
  const size = parts.reduce((sum, part) => sum + 2 + part.length, 0);
  const out = new Uint8Array(new ArrayBuffer(head.length + 2 + size));
  out.set(head, 0);
  let at = head.length;
  out[at++] = 0x00;
  out[at++] = 0x02;
  for (const part of parts) {
    if (part.length > U16_MAX) throw new AadFieldError();
    out[at++] = part.length >> 8;
    out[at++] = part.length & 0xff;
    out.set(part, at);
    at += part.length;
  }
  return out;
}

// Ids are bound lower-cased: the server's uuid columns answer in lower case
// whatever case a device sent (iPhone's uuidString is upper case).
const id = (value: string) =>
  typeof value === 'string' ? value.toLowerCase() : value;

/** A v2 message's associated data (D325, D326). */
export function messageAadV2(b: MessageBinding): Uint8Array<ArrayBuffer> {
  return aadV2('msg', [
    id(b.conversationId),
    id(b.senderId),
    id(b.clientId),
    b.type,
    b.epoch,
    b.revision,
  ]);
}

/** A group description's or picture's associated data (D325). */
export function groupAadV2(b: GroupBinding): Uint8Array<ArrayBuffer> {
  return aadV2('group', [
    id(b.conversationId),
    id(b.clientId),
    b.which,
    b.epoch,
    b.revision,
  ]);
}

/** A v2 family record's associated data (D325, D329). */
export function appAadV2(b: AppBinding): Uint8Array<ArrayBuffer> {
  return aadV2('app', [
    id(b.familyId),
    b.kind,
    id(b.parentId),
    id(b.recordId),
    b.epoch,
  ]);
}

/**
 * D328: a v1 envelope opens, marked not bound, only under an epoch at or
 * below this device's ceiling for its chat or family; above it, it is a
 * downgrade and refused.
 */
export function v1Decision(
  epoch: number,
  ceiling: number
): 'open-unbound' | 'refuse' {
  return epoch <= ceiling ? 'open-unbound' : 'refuse';
}

/** D326 (c): never a lower revision than the highest this device showed. */
export function revisionDecision(
  highestShown: number | undefined,
  got: number
): 'ok' | 'stale' {
  return highestShown !== undefined && got < highestShown ? 'stale' : 'ok';
}

/** What a scope's v1 ceiling is worked out from (D328). */
export interface CeilingInputs {
  /** The current epoch of this session's latest fresh (not cached) keys answer. */
  freshCurrent: number | null;
  /** The epoch the server says v2 began in the scope (`v2SinceEpoch`). */
  v2Since: number | null;
  /** The lowest epoch this device has opened a v2 envelope under (kept). */
  lowestV2: number | null;
  /** The ceiling kept from the last fresh answer, in an earlier session. */
  kept: number | null;
  /** Before any answer at all: a guess (the envelope's or highest held). */
  guess: number;
}

/**
 * D328: the newest epoch under which this device opens v1. It follows each
 * fresh keys answer's current epoch (rotations happen without anyone
 * writing v2, and an older app's v1 there is genuine), capped below where
 * the server says v2 began, and never at or above an epoch this device
 * has opened v2 under (an M76 app only writes v2 into an epoch with no v1
 * in it, D328). Without a fresh answer this session: the kept one, else
 * the guess (never written).
 */
export function v1CeilingFrom(input: CeilingInputs): number {
  let ceiling = input.freshCurrent ?? input.kept ?? input.guess;
  if (input.v2Since !== null) ceiling = Math.min(ceiling, input.v2Since - 1);
  if (input.lowestV2 !== null) ceiling = Math.min(ceiling, input.lowestV2 - 1);
  return ceiling;
}

/** Hex of some bytes, for the vector. */
export function toHex(bytes: Uint8Array): string {
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}
